import { dump } from 'js-yaml';
import { FC, useEffect, useState } from 'react';
import { matchPath, Redirect, Route, Switch, useHistory, useRouteMatch } from 'react-router-dom';

import {
    ActionMenu,
    BodyText,
    Breadcrumb,
    Button,
    Headline,
    Page,
    Stack,
    Tab,
    TabGroup,
    Tag,
} from '@servicetitan/design-system';
import { useDependencies } from '@servicetitan/react-ioc';

import { DOCUMENTATION_API_TOKEN, OpenApiDocument } from '../../api/documentation.api';
import { downloadFile, PageSpinner } from '../../common';
import { overviews } from '../assets/overviews';
import { ApiOperationGroupModel, getApiOperationGroups } from '../models/api-operation-model';
import { getWebhookOperations, WebhookOperationModel } from '../models/webhook-operation-model';
import { AnvilMarkdown } from './anvil-markdown';
import { ApiListSidebar } from './api-list-sidebar';
import { ApiOperationList, ApiOperationListSidebar } from './api-operation-list';
import { OperationDetails } from './operation-details';
import { WebhookOperationList, WebhookOperationListSidebar } from './webhook-operation-list';

interface State {
    doc: OpenApiDocument;
    apiOperationGroups: ApiOperationGroupModel[];
    webhookOperations: WebhookOperationModel[];
}

export const ApiDetailsPage: FC<{ apiId: string }> = ({ apiId }) => {
    const [documentationApi] = useDependencies(DOCUMENTATION_API_TOKEN);
    const history = useHistory();
    const match = useRouteMatch();
    const [state, setState] = useState<State | null>(null);
    const [openApiDocumentMenuOpen, setOpenApiDocumentMenuOpen] = useState(false);

    useEffect(() => {
        documentationApi.getApi(apiId).then(doc => {
            setState({
                doc,
                apiOperationGroups: getApiOperationGroups(doc),
                webhookOperations: getWebhookOperations(doc),
            });
        });
    }, [documentationApi, apiId]);

    if (!state) {
        return <PageSpinner />;
    }

    const overviewPath = `${match.url}/overview`;
    const apiEndpointsPath = `${match.url}/endpoints`;
    const webhooksPath = `${match.url}/webhooks`;
    const { doc, apiOperationGroups, webhookOperations } = state;
    return (
        <Page
            header={
                <Stack direction="column">
                    <Stack.Item>
                        <Breadcrumb className="m-b-2">
                            <Breadcrumb.Link
                                label="Reference"
                                onClick={() => {
                                    history.push('/docs/apis');
                                }}
                            />
                            <Breadcrumb.Link label={doc.info.title} />
                        </Breadcrumb>
                    </Stack.Item>
                    <Stack direction="row">
                        <Stack.Item fill>
                            <Headline size="large">{doc.info.title}</Headline>
                        </Stack.Item>
                        <ActionMenu
                            trigger={
                                <Button
                                    primary
                                    outline
                                    iconName={
                                        openApiDocumentMenuOpen ? 'expand_less' : 'expand_more'
                                    }
                                    iconPosition="right"
                                    onClick={() => {
                                        setOpenApiDocumentMenuOpen(open => !open);
                                    }}
                                >
                                    OpenAPI Document
                                </Button>
                            }
                            width="xs"
                            open={openApiDocumentMenuOpen}
                            onClickOutside={() => {
                                setOpenApiDocumentMenuOpen(false);
                            }}
                        >
                            <ActionMenu.Item
                                onClick={() => {
                                    const json = JSON.stringify(doc, undefined, 2);
                                    downloadFile(json, 'application/json', `${apiId}.json`);
                                    setOpenApiDocumentMenuOpen(false);
                                }}
                            >
                                Download JSON
                            </ActionMenu.Item>
                            <ActionMenu.Item
                                onClick={() => {
                                    const yaml = dump(doc, { lineWidth: -1 });
                                    downloadFile(yaml, 'application/yaml', `${apiId}.yaml`);
                                    setOpenApiDocumentMenuOpen(false);
                                }}
                            >
                                Download YAML
                            </ActionMenu.Item>
                        </ActionMenu>
                    </Stack>
                    {doc.info.description && (
                        <BodyText className="w-75" subdued>
                            {doc.info.description}
                        </BodyText>
                    )}
                </Stack>
            }
            sidebar={
                <Switch>
                    <Route
                        path={`${apiEndpointsPath}/:operationId`}
                        exact
                        render={({
                            match: {
                                params: { operationId },
                            },
                        }) => (
                            <ApiOperationListSidebar
                                apiEndpointsPath={apiEndpointsPath}
                                apiOperationGroups={apiOperationGroups}
                                activeOperationId={operationId}
                            />
                        )}
                    />
                    <Route
                        path={`${webhooksPath}/:name`}
                        exact
                        render={({
                            match: {
                                params: { name },
                            },
                        }) => (
                            <WebhookOperationListSidebar
                                webhooksPath={webhooksPath}
                                webhookOperations={webhookOperations}
                                activeName={name}
                            />
                        )}
                    />
                    <Route path="/" component={ApiListSidebar} />
                </Switch>
            }
        >
            <TabGroup id="tabs" className="m-b-3">
                {overviews.has(apiId) && (
                    <Tab
                        active={!!matchPath(history.location.pathname, overviewPath)}
                        onClick={() => {
                            history.push(overviewPath);
                        }}
                    >
                        Overview
                    </Tab>
                )}
                <Tab
                    active={!!matchPath(history.location.pathname, apiEndpointsPath)}
                    onClick={() => {
                        history.push(apiEndpointsPath);
                    }}
                >
                    API Endpoints
                </Tab>
                {webhookOperations.length > 0 && (
                    <Tab
                        active={!!matchPath(history.location.pathname, webhooksPath)}
                        onClick={() => {
                            history.push(webhooksPath);
                        }}
                    >
                        Webhooks
                        <Tag
                            className="m-l-1"
                            color="info"
                            title="Webhooks may be updated any time and may not become generally available."
                            compact
                        >
                            beta
                        </Tag>
                    </Tab>
                )}
            </TabGroup>
            <Switch>
                {overviews.has(apiId) && (
                    <Route path={overviewPath} exact>
                        <AnvilMarkdown>{overviews.get(apiId)!}</AnvilMarkdown>
                    </Route>
                )}
                <Route path={apiEndpointsPath} exact>
                    <ApiOperationList
                        apiEndpointsPath={apiEndpointsPath}
                        apiOperationGroups={apiOperationGroups}
                    />
                </Route>
                <Route
                    path={`${apiEndpointsPath}/:operationId`}
                    exact
                    render={({
                        match: {
                            params: { operationId },
                        },
                    }) => {
                        const apiOperation = apiOperationGroups
                            .flatMap(apiOperationGroup => apiOperationGroup.operations)
                            .find(apiOperation => apiOperation.operationId === operationId);
                        return apiOperation ? (
                            <OperationDetails
                                key={apiOperation.operationId}
                                backLinkLabel="API Endpoints"
                                backLinkPath={apiEndpointsPath}
                                title={`${apiOperation.operationId} Endpoint`}
                                method={apiOperation.method}
                                servers={doc.servers.map(server => ({
                                    url: server.url + apiOperation.path,
                                    description: server.description,
                                }))}
                                operation={apiOperation}
                                schemas={doc.components.schemas}
                                securitySchemes={doc.components.securitySchemes}
                                securityRequirements={apiOperation.security}
                            />
                        ) : (
                            <Redirect to={apiEndpointsPath} />
                        );
                    }}
                />
                {webhookOperations.length > 0 && (
                    <Route path={webhooksPath} exact>
                        <WebhookOperationList
                            webhooksPath={webhooksPath}
                            webhookOperations={webhookOperations}
                        />
                    </Route>
                )}
                <Route
                    path={`${webhooksPath}/:name`}
                    exact
                    render={({
                        match: {
                            params: { name },
                        },
                    }) => {
                        const webhookOperation = webhookOperations.find(
                            webhookOperation => webhookOperation.name === name,
                        );
                        return webhookOperation ? (
                            <OperationDetails
                                key={webhookOperation.name}
                                backLinkLabel="Webhooks"
                                backLinkPath={webhooksPath}
                                title={`${webhookOperation.name} Webhook`}
                                method={webhookOperation.method}
                                servers={[{ url: '<your callback URL>' }]}
                                operation={webhookOperation}
                                schemas={doc.components.schemas}
                                securitySchemes={doc.components.securitySchemes}
                                securityRequirements={webhookOperation.security}
                            />
                        ) : (
                            <Redirect to={webhooksPath} />
                        );
                    }}
                />
                <Route path="/">
                    <Redirect to={overviews.has(apiId) ? overviewPath : apiEndpointsPath} />
                </Route>
            </Switch>
        </Page>
    );
};
ApiDetailsPage.displayName = 'ApiDetailsPage';
